import React, { useMemo } from "react"

import { MultiSelect } from "~/ui-rtk/components/ui/controls"
import { type TMultiSelectOption } from "~/ui-rtk/components/ui/controls/MultiSelect"
import type { TableVisualizationColumnFilter } from "~/ui-rtk/constants/table-visualization-types"
import { type TFilterColumnConfig } from "../types"

type SelectFilterProps = {
  column: TFilterColumnConfig
  tableFilter?: TableVisualizationColumnFilter | null
  onChange: (key: string, value: string[]) => void
  getOptions?: (columnKey: string) => TMultiSelectOption[]
}

const SelectFilter: React.FC<SelectFilterProps> = ({
  tableFilter,
  column: columnDef,
  onChange,
  getOptions,
}) => {
  const defaultValues = (tableFilter?.value as string[]) ?? []

  const options = useMemo(() => {
    if (getOptions) {
      // Use filterKey if provided, otherwise use key
      const optionsKey = columnDef.filterKey || columnDef.key
      return getOptions(optionsKey)
    }
    // Default empty options if no getOptions function provided
    return []
  }, [getOptions, columnDef.key, columnDef.filterKey])

  // Convert string values to TMultiSelectOption objects for the component
  const selectedOptions = useMemo(
    () => options.filter(option => defaultValues.includes(option.value)),
    [options, defaultValues],
  )

  const handleSelectionChange = (selectedOptions: TMultiSelectOption[]) => {
    const values = selectedOptions.map(option => option.value)
    onChange(columnDef.key, values)
  }

  return (
    <div className="space-y-3">
      <label className="ml-1 font-semibold">
        {columnDef.header || columnDef.key}
      </label>
      <MultiSelect
        options={options}
        value={selectedOptions}
        onChange={handleSelectionChange}
        className="bg-basic-blue"
      />
    </div>
  )
}

export default SelectFilter
